import { Checkbox, CheckboxSize } from "@components/Checkbox";
import { TestimonialPromt } from "@components/TestimonialPromt";
import { TextArea } from "@components/TextInput";
import { DonateFormType } from "@components/donate/DonateV3/types";
import styled from "@emotion/styled";
import { UseFormReturn, Controller } from "react-hook-form";

import { ContextNonprofit } from "src/context/NonprofitsContext/types";
import { colorCssVars } from "src/theme/color";
import { horizontalStackCss, verticalStackCss } from "src/theme/spacing";
import { TextSize, textSizeCss } from "src/theme/text";
import { useUserLayer } from "src/utility/abtesting";

const DEFAULT_PROMPT_TEXT = `**Help {nonprofitName} by sharing why you support them with the world**`;

const StyledCheckbox = styled(Checkbox)`
  ${horizontalStackCss.xs};
  ${textSizeCss.s};
  align-items: center;
`;

const StyledLabel = styled.label`
  color: var(${colorCssVars.text.body});
  ${textSizeCss[TextSize.s]};
`;

interface TestimonyInputProps {
  nonprofit: ContextNonprofit;
  form: UseFormReturn<DonateFormType>;
}

export const TestimonyInput = ({ nonprofit, form }: TestimonyInputProps) => {
  const {
    control,
    formState: { errors },
  } = form;
  const includeCommentText = form.watch("includeCommentText");

  const promptText = useUserLayer(
    "testimonial_sharing",
    nonprofit.metadata?.disablePublicTestimony || !includeCommentText
  ).get<string>("first_step", DEFAULT_PROMPT_TEXT);

  if (nonprofit.metadata?.disablePublicTestimony) {
    return null;
  }

  return (
    <div css={verticalStackCss.l}>
      <Controller
        control={control}
        key="includeCommentText"
        name="includeCommentText"
        render={({ field: { onChange, value } }) => (
          <StyledCheckbox
            data-tname="includeCommentText__Chekbox"
            checked={value}
            size={CheckboxSize.LARGE}
            onClick={() => {
              onChange(!value);
            }}
            css={{
              color: `var(${
                value ? colorCssVars.text.body : colorCssVars.text.secondary
              })`,
            }}
          >
            Add public testimony
          </StyledCheckbox>
        )}
      />
      {includeCommentText && (
        <Controller
          control={control}
          key="testimonyText"
          name="commentText"
          render={({ field: { onChange, value } }) => (
            <div css={verticalStackCss.xs}>
              <StyledLabel htmlFor="testimonyText">
                <TestimonialPromt
                  string={promptText}
                  nonprofitName={nonprofit.name}
                />
              </StyledLabel>
              <TextArea
                rows={3}
                label="Note"
                data-tname="testimonyText"
                id="testimonyText"
                value={value}
                onChange={onChange}
                collapseDescriptionSpace
                validationStatus={
                  errors.commentText
                    ? {
                        success: false,
                        message: errors.commentText.message,
                      }
                    : undefined
                }
              />
            </div>
          )}
        />
      )}
    </div>
  );
};
